import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ApiErrors, RequestTypeShort, StoreId } from '../types';
import { errorMessages, errorMessageMeta, ErrorMetadata } from '../data/errors/errors.data';
import { interpolateErrorMessage } from '../data/errors/interpolateErrorMessage';

export interface ErrorState {
  invalidPatient: boolean;
  message?: string | null;
  attemptedUrl?: string | null;
  errorHttpReferrer?: string | null;
  code: string | null;
  meta: Record<string, unknown> | null;
  errors?: ApiErrors;
  isNhsError?: boolean;
}

export type ErrorPayload = Partial<ErrorState>;

export interface NormalisedErrorItem extends ErrorMetadata {
  code: string;
  message?: string;
  requestType?: RequestTypeShort | StoreId;
  [key: string]: unknown;
}

type ErrorObj = Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function resolveMeta(code: string): ErrorMetadata {
  return errorMessageMeta[code] ?? errorMessageMeta['unknown-error'] ?? ({} as ErrorMetadata);
}

// The NHS App variants of a code's copy carry the same placeholders as `message`.
function interpolateMeta(meta: ErrorMetadata, values: Record<string, unknown>): ErrorMetadata {
  const { messageNhsApp, messageNhsAppWeb } = meta;

  return {
    ...meta,
    ...(messageNhsApp ? { messageNhsApp: interpolateErrorMessage(messageNhsApp, values) } : {}),
    ...(messageNhsAppWeb
      ? { messageNhsAppWeb: interpolateErrorMessage(messageNhsAppWeb, values) }
      : {}),
  };
}

export const useErrorStore = defineStore('error', () => {
  const isNhsError = ref<boolean>(false);
  const isNhsAppError = ref<boolean>(false);
  const invalidPatient = ref<ErrorState['invalidPatient']>(false);
  const message = ref<ErrorState['message']>(null);
  const errors = ref<NormalisedErrorItem[]>([]);

  const attemptedUrl = ref<ErrorState['attemptedUrl']>(null);
  const errorHttpReferrer = ref<ErrorState['errorHttpReferrer']>(null);
  const code = ref<ErrorState['code']>(null);
  const meta = ref<ErrorState['meta']>(null);

  // --- Reset everything ---------------------------------
  const reset = () => {
    invalidPatient.value = false;
    message.value = null;
    code.value = null;
    meta.value = null;
    errors.value = null;
    attemptedUrl.value = null;
    errorHttpReferrer.value = null;
  };

  function normaliseErrors(raw: RawErrors): Array<ErrorObj & { code: string; message: string }> {
    const out: Array<ErrorObj & { code: string; message: string }> = [];

    if (Array.isArray(raw)) {
      for (const item of raw) {
        if (!isRecord(item)) continue;

        const errorMap = errorMessages as Record<string, string>;
        const rawCode = typeof item.code === 'string' ? item.code.trim() : '';
        const finalCode =
          rawCode in errorMap
            ? rawCode
            : isNhsError.value || isNhsAppError.value
              ? 'unknown-error-nhs-param'
              : 'unknown-error';

        const rawMessage =
          item.message ??
          (errorMessages as Record<string, string>)[finalCode] ??
          errorMessages['unknown-error'];

        // ENG-21798 The copy is a flat string, so a value the API sends alongside the
        // code (the lockout's `lockedUntil`) reaches the patient through a placeholder.
        const message =
          typeof rawMessage === 'string'
            ? interpolateErrorMessage(rawMessage, item)
            : (rawMessage as string);

        const mergedMeta = interpolateMeta(resolveMeta(finalCode), item);

        out.push({ ...mergedMeta, ...(item as ErrorObj), code: finalCode, message });
      }
      return out.length ? out : [{ code: 'unknown-error', message: 'Unknown error' } as any];
    }

    // B) Field-map: { prescriptionIds: ["..."], prescriptionAcuteIds: ["..."] }
    if (isRecord(raw)) {
      for (const [key, value] of Object.entries(raw)) {
        const message = Array.isArray(value)
          ? value
              .filter((v): v is string => typeof v === 'string')
              .map((v) => v.trim())
              .filter(Boolean)
              .join(', ')
          : typeof value === 'string'
            ? value.trim()
            : String(value);

        out.push({
          code: 'unknown-error',
          message: `${key}: ${message}`,
          key,
        } as any);
      }
      return out.length ? out : [{ code: 'unknown-error', message: 'Unknown error' } as any];
    }

    return [{ code: 'unknown-error', message: 'Unknown error and code' } as any];
  }

  const setError = (payload: ErrorPayload) => {
    if (payload.invalidPatient !== undefined) invalidPatient.value = payload.invalidPatient;
    if (payload.message !== undefined) message.value = payload.message;
    if (payload.code !== undefined) code.value = payload.code;
    if (payload.meta !== undefined) meta.value = payload.meta;
  };

  type ErrorMap = Record<string, unknown>;
  type RawErrors = NormalisedErrorItem[] | ErrorMap;

  const setErrors = (payloadErrors: RawErrors): NormalisedErrorItem => {
    errors.value = normaliseErrors(payloadErrors);
    return errors.value[0];
  };

  const setIsNhsError = (value: boolean) => {
    isNhsError.value = value;
  };

  const setIsNhsAppError = (value: boolean) => {
    isNhsAppError.value = value;
  };

  const setAttemptedUrl = (url: string): string => (attemptedUrl.value = url);
  const setErrorHttpReferrer = (url: string): string => (errorHttpReferrer.value = url);

  const getAttemptedUrl = (): string => attemptedUrl.value;
  const getErrorHttpReferrer = (): string => errorHttpReferrer.value;

  const getMainMessage = (): string => message.value;

  const getFirstError = (): NormalisedErrorItem => errors.value[0];
  const getFirstTitle = (): string => errors.value[0]?.title;
  const getFirstCode = (): string => errors.value[0]?.code;
  const getShowStartButton = (): boolean => errors.value[0]?.showStartButton;
  const getShowBackButton = (): boolean => errors.value[0]?.showBackButton !== false;

  const getFirstMessage = (): string => errors.value[0]?.message;
  const getFirstMessageNhsAppWeb = (): string =>
    errors.value[0]?.messageNhsAppWeb || getFirstMessage();

  const getFirstMessageNhsApp = (): string => errors.value[0]?.messageNhsApp || getFirstMessage();

  const getFirstRequestType = (): string => errors.value[0]?.requestType;
  const getComponents = () => errors.value[0]?.components;

  const clearError = () => reset();

  const dontShowCodeFor = ['consent_required'];
  const showNhsError = () => (dontShowCodeFor.includes(getFirstCode()) ? false : isNhsError.value);
  const showCode = () => {
    if (!isNhsError.value && !getFirstMessage() && getFirstCode()) return true;
  };

  return {
    // state
    attemptedUrl,
    errorHttpReferrer,
    invalidPatient,
    isNhsError,
    isNhsAppError,
    message,
    code,
    meta,
    errors,
    errorMessages,
    //
    getErrorHttpReferrer,
    getAttemptedUrl,
    getComponents,
    getFirstCode,
    getFirstError,
    getFirstMessage,
    getFirstMessageNhsAppWeb,
    getFirstMessageNhsApp,
    getFirstRequestType,
    getFirstTitle,
    getMainMessage,
    getShowStartButton,
    getShowBackButton,
    showCode,
    showNhsError,
    // methods
    setAttemptedUrl,
    setErrorHttpReferrer,
    setError,
    setErrors,
    setIsNhsError,
    setIsNhsAppError,
    clearError,
  };
});
